import type { ReadonlySignal } from '@preact/signals-core';
import { computed } from '@preact/signals-core';

import type { ViewManager } from '../../core/view-manager/view-manager.js';
import type { Property } from '../../core/view-manager/property.js';
import { PropertyBase } from '../../core/view-manager/property.js';
import { SingleViewBase } from '../../core/view-manager/single-view.js';
import type {
  GanttBar,
  GanttScaleName,
  GanttStoredViewData,
  GanttUndatedRow,
} from './types.js';

export type GanttDateMapping =
  | {
      status: 'ready';
      propertyId: string;
      /** the column is a `date-range`, so it carries both ends on its own */
      isRange: boolean;
    }
  | { status: 'setup'; propertyId?: string };

const DATE_PROPERTY_TYPES = new Set(['date', 'date-range']);

const readTimestamp = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    for (const key of ['start', 'startAt', 'from']) {
      const candidate = record[key];
      if (typeof candidate === 'number' && Number.isFinite(candidate)) {
        return candidate;
      }
    }
  }
  return undefined;
};

const readEndTimestamp = (value: unknown): number | undefined => {
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    for (const key of ['end', 'endAt', 'to']) {
      const candidate = record[key];
      if (typeof candidate === 'number' && Number.isFinite(candidate)) {
        return candidate;
      }
    }
  }
  return readTimestamp(value);
};

/**
 * Gantt view ("Инфокарта") data model.
 *
 * Every row with a date lands on the timeline; rows without any date are kept
 * apart in `undatedRows$` so the UI can list them under «Без дат» instead of
 * dropping them silently.
 */
export class GanttSingleView extends SingleViewBase<GanttStoredViewData> {
  constructor(viewManager: ViewManager, viewId: string) {
    super(viewManager, viewId);
  }

  get type(): string {
    return this.data$.value?.mode ?? 'gantt';
  }

  readonly$ = computed(() => this.manager.readonly$.value);

  propertiesRaw$ = computed<Property[]>(() => {
    return this.dataSource.properties$.value.map(id =>
      this.propertyGetOrCreate(id)
    );
  });

  properties$ = this.propertiesRaw$;

  detailProperties$ = computed(() => {
    return this.propertiesRaw$.value.filter(
      property => property.type$.value !== 'title'
    );
  });

  mainProperties$ = computed(() => ({
    titleColumn: this.titleColumnId$.value,
  }));

  titleColumnId$ = computed<string | undefined>(() => {
    return (
      this.data$.value?.header?.titleColumn ??
      this.dataSource.properties$.value.find(
        id => this.dataSource.propertyTypeGet(id) === 'title'
      )
    );
  });

  dateProperties$ = computed<Property[]>(() =>
    this.propertiesRaw$.value.filter(property =>
      DATE_PROPERTY_TYPES.has(property.type$.value)
    )
  );

  private resolveMapping(propertyId?: string): GanttDateMapping {
    if (propertyId && this.dataSource.properties$.value.includes(propertyId)) {
      const type = this.dataSource.propertyTypeGet(propertyId);
      if (type && DATE_PROPERTY_TYPES.has(type)) {
        return { status: 'ready', propertyId, isRange: type === 'date-range' };
      }
    }
    return { status: 'setup', propertyId };
  }

  dateMapping$: ReadonlySignal<GanttDateMapping> = computed(() =>
    this.resolveMapping(this.data$.value?.date?.startColumnId)
  );

  /** Fallback: the first date column, so a fresh view shows a chart right away. */
  effectiveMapping$: ReadonlySignal<GanttDateMapping> = computed(() => {
    const mapping = this.dateMapping$.value;
    if (mapping.status === 'ready') return mapping;
    const first = this.dataSource.properties$.value.find(id =>
      DATE_PROPERTY_TYPES.has(this.dataSource.propertyTypeGet(id) ?? '')
    );
    return first ? this.resolveMapping(first) : mapping;
  });

  /** Chart granularity, persisted in the view data so it survives reloads. */
  scale$: ReadonlySignal<GanttScaleName> = computed(
    () => this.data$.value?.scale ?? 'day'
  );

  setScale(scale: GanttScaleName) {
    this.dataUpdate(() => ({ scale }));
  }

  highlightWeekends$ = computed(
    () => this.data$.value?.highlightWeekends ?? true
  );

  setHighlightWeekends(value: boolean) {
    this.dataUpdate(() => ({ highlightWeekends: value }));
  }

  setStartDateColumn(propertyId: string | undefined) {
    this.dataUpdate(data => ({
      date: { ...data.date, startColumnId: propertyId },
    }));
  }

  private titleOf(rowId: string): string {
    const columnId = this.titleColumnId$.value;
    if (!columnId) return '';
    const cell = this.cellGetOrCreate(rowId, columnId);
    return String(cell.stringValue$.value ?? '');
  }

  rowDate$(rowId: string) {
    const mapping = this.effectiveMapping$.value;
    if (mapping.status !== 'ready') return undefined;
    return this.cellGetOrCreate(rowId, mapping.propertyId).jsonValue$.value;
  }

  bars$ = computed<GanttBar[]>(() => {
    const mapping = this.effectiveMapping$.value;
    if (mapping.status !== 'ready') return [];

    const bars = this.rows$.value.flatMap(row => {
      const value = this.cellGetOrCreate(row.rowId, mapping.propertyId)
        .jsonValue$.value;
      const start = readTimestamp(value);
      if (start === undefined) return [];

      const end = readEndTimestamp(value);
      const endAt = end !== undefined && end >= start ? end : start;

      return [
        {
          id: `gantt:${row.rowId}`,
          rowId: row.rowId,
          title: this.titleOf(row.rowId) || 'Без названия',
          // raw cell timestamps; the renderer floors them to day columns
          startAt: start,
          endAt,
          hasEnd: end !== undefined && end > start,
        } satisfies GanttBar,
      ];
    });
    // a Gantt reads chronologically — order rows by their start date
    return bars.sort(
      (a, b) =>
        a.startAt - b.startAt ||
        a.title.localeCompare(b.title) ||
        a.rowId.localeCompare(b.rowId)
    );
  });

  /** Rows that carry no date at all — shown under «Без дат». */
  undatedRows$ = computed<GanttUndatedRow[]>(() => {
    const mapping = this.effectiveMapping$.value;
    if (mapping.status !== 'ready') {
      return this.rows$.value.map(row => ({
        rowId: row.rowId,
        title: this.titleOf(row.rowId) || 'Без названия',
      }));
    }

    return this.rows$.value.flatMap(row => {
      const value = this.cellGetOrCreate(row.rowId, mapping.propertyId)
        .jsonValue$.value;
      if (readTimestamp(value) !== undefined) return [];
      return [
        {
          rowId: row.rowId,
          title: this.titleOf(row.rowId) || 'Без названия',
        },
      ];
    });
  });

  /** Full visible range of the chart (raw ms, untrimmed). */
  range$ = computed<{ start: number; end: number } | null>(() => {
    const bars = this.bars$.value;
    if (!bars.length) return null;
    let start = Infinity;
    let end = -Infinity;
    for (const bar of bars) {
      start = Math.min(start, bar.startAt);
      end = Math.max(end, bar.endAt);
    }
    return { start, end };
  });

  isShow(_rowId: string): boolean {
    return true;
  }

  propertyGetOrCreate(propertyId: string): Property {
    return new GanttProperty(this, propertyId);
  }

  /** Sets the date of a row; `endAt` is used only for `date-range` columns. */
  setRowDate(rowId: string, startAt: number, endAt?: number) {
    const mapping = this.effectiveMapping$.value;
    if (mapping.status !== 'ready' || this.readonly$.value) return;
    const cell = this.cellGetOrCreate(rowId, mapping.propertyId);
    if (mapping.isRange) {
      cell.jsonValueSet({ start: startAt, end: endAt ?? startAt });
    } else {
      cell.jsonValueSet(startAt);
    }
  }
}

export class GanttProperty extends PropertyBase {
  hide$ = computed(() => false);

  constructor(view: GanttSingleView, propertyId: string) {
    super(view as never, propertyId);
  }

  hideSet(_hide: boolean): void {}

  move(_position: unknown): void {}
}
